import { useState, useEffect } from "react";
import axios from "axios";
import { Search, Zap, Phone, MapPin, Globe, ExternalLink, Truck } from "lucide-react";
import { LEGACY_PIPELINE, getLegacyPipelineIdx } from "@/components/StatusPipeline";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDT = d => d ? new Date(d).toLocaleString("en-IN", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "";
const fmtD  = d => d ? new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"long", year:"numeric" }) : "";

const CHARGER_ICONS = { "Small Charger":"🔋", "Fast Charger":"⚡", "Rickshaw Charger":"🛺" };
const WARRANTY_COLORS = {
  "Under Warranty":  "bg-green-50 text-green-700 border-green-200",
  "Out of Warranty": "bg-amber-50 text-amber-700 border-amber-200",
  "Expired":         "bg-rose-50 text-rose-600 border-rose-200",
  "XP Charger":      "bg-blue-50 text-blue-700 border-blue-200",
};

export default function SCTrackPage() {
  const [searchInput, setSearchInput] = useState("");
  const [job,     setJob]     = useState(null);
  const [sc,      setSC]      = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings`).then(r => setSC(r.data)).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    const jobNo  = params.get("job");
    if (jobNo) { setSearchInput(jobNo); doSearch(jobNo); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doSearch = async (val) => {
    const q = (val || searchInput).trim();
    if (!q) return;
    setLoading(true); setError(null); setJob(null);
    try {
      const r = await axios.get(`${API}/sc/track?job=${encodeURIComponent(q)}`);
      setJob(r.data);
    } catch (err) {
      setError(err.response?.status === 404
        ? `No SC job found for "${q}". Please check the job number.`
        : "Failed to load tracking info. Please try again.");
    } finally { setLoading(false); }
  };

  const currentIdx = job ? getLegacyPipelineIdx(job.current_status, job.status_history || []) : -1;
  const historyMap = job ? Object.fromEntries((job.status_history || []).map(h => [h.status, h])) : {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <img src="/logo.jpg" alt="XSTRONG POWER" className="h-9 w-auto object-contain" />
          <div className="flex-1">
            <p className="font-bold text-sm text-slate-900" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
              XSTRONG POWER EV CHARGER
            </p>
            <p className="text-xs text-green-600 font-medium">SC Job Tracking</p>
          </div>
          <a href="/track" className="text-xs text-slate-400 hover:text-green-700 transition-colors hidden sm:block">
            Track Regular Job →
          </a>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Search */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-green-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Zap size={26} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
            Track Your SC Job
          </h1>
          <p className="text-slate-500 text-sm">Enter your SC job number (e.g. SC-0001)</p>
        </div>

        <form onSubmit={e => { e.preventDefault(); doSearch(); }} className="flex gap-2 mb-8">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={searchInput} onChange={e => setSearchInput(e.target.value)}
              placeholder="e.g. SC-0001"
              className="w-full pl-9 pr-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-100 font-mono"
            />
          </div>
          <button type="submit" disabled={loading}
            className="px-5 py-3 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50">
            {loading ? "Searching…" : "Track"}
          </button>
        </form>

        {/* Error */}
        {error && (
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 text-sm text-rose-700 mb-6 text-center">
            {error}
          </div>
        )}

        {/* Job Result */}
        {job && (
          <div className="space-y-4">
            {/* Header Card */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-green-600 px-5 py-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-green-200 font-medium uppercase tracking-wide mb-1">SC Job Number</p>
                    <p className="text-2xl font-bold text-white font-mono" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
                      {job.sc_no}
                    </p>
                    <p className="text-xs text-green-200 mt-1">Received: {fmtD(job.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-green-200 mb-1">Current Status</p>
                    <span className="inline-flex items-center gap-1.5 bg-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-full border border-white/30">
                      {LEGACY_PIPELINE[currentIdx]?.emoji || "📋"} {job.current_status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Progress bar */}
              <div className="px-5 py-3 bg-slate-50 border-b border-slate-100">
                <div className="flex justify-between text-[10px] text-slate-400 mb-1.5">
                  <span>Pick Up Request</span>
                  <span className="font-medium text-green-600">
                    {Math.round(((currentIdx + 1) / LEGACY_PIPELINE.length) * 100)}% complete
                  </span>
                  <span>In Transit</span>
                </div>
                <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full transition-all"
                    style={{ width: `${((currentIdx + 1) / LEGACY_PIPELINE.length) * 100}%` }} />
                </div>
              </div>
            </div>

            {/* Customer Details */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center">
                  <Phone size={14} className="text-slate-600" />
                </div>
                <h2 className="text-sm font-bold text-slate-900" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
                  Customer Details
                </h2>
              </div>
              <div className="space-y-2.5">
                {job.customer?.name && (
                  <div className="flex justify-between items-center py-1.5 border-b border-slate-50">
                    <span className="text-xs text-slate-400">Name</span>
                    <span className="text-sm font-semibold text-slate-800">{job.customer.name}</span>
                  </div>
                )}
                {job.customer?.phone && (
                  <div className="flex justify-between items-center py-1.5 border-b border-slate-50">
                    <span className="text-xs text-slate-400">Phone</span>
                    <a href={`tel:${job.customer.phone}`}
                      className="text-sm font-semibold text-green-700 hover:text-green-800 font-mono">
                      {job.customer.phone}
                    </a>
                  </div>
                )}
                {/* Full address block */}
                {(job.customer?.address || job.customer?.landmark || job.customer?.city || job.customer?.state || job.customer?.pincode) && (
                  <div className="pt-1.5">
                    <p className="text-xs text-slate-400 mb-1.5">Address</p>
                    <div className="bg-slate-50 rounded-xl px-3 py-2.5 space-y-0.5">
                      {job.customer?.address  && <p className="text-sm text-slate-800">{job.customer.address}</p>}
                      {job.customer?.landmark && <p className="text-xs text-slate-500">Near: {job.customer.landmark}</p>}
                      {(job.customer?.city || job.customer?.state || job.customer?.pincode) && (
                        <p className="text-sm text-slate-700">
                          {[job.customer.city, job.customer.state, job.customer.pincode].filter(Boolean).join(", ")}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Charger Details — table format */}
            {(job.chargers || []).length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="flex items-center gap-2.5 px-5 py-4 border-b border-slate-100">
                  <div className="w-8 h-8 rounded-xl bg-green-600 flex items-center justify-center flex-shrink-0">
                    <Zap size={15} className="text-white" />
                  </div>
                  <h2 className="text-sm font-bold text-slate-900" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
                    Charger Details
                  </h2>
                  <span className="text-xs text-slate-400 ml-1">
                    {job.chargers.length} unit{job.chargers.length !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50/60">
                        {["#","Type","Battery Cut-off / Amp","Serial Number","Body Type","Warranty","Purchase"].map(h => (
                          <th key={h} className="text-left text-[10px] font-bold text-slate-400 uppercase tracking-wide px-3 py-2.5 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {job.chargers.map((c, i) => {
                        const monthName = c.mfg_month
                          ? ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][parseInt(c.mfg_month) - 1] || ""
                          : "";
                        const purchaseStr = [monthName, c.mfg_year].filter(Boolean).join(" ");
                        // Hide "XP Charger" warranty status on public page
                        const showWarranty = c.warranty_status && c.warranty_status !== "XP Charger";
                        return (
                          <tr key={i} className="border-b border-slate-50 last:border-0">
                            <td className="px-3 py-3">
                              <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 text-xs font-bold inline-flex items-center justify-center">{i + 1}</span>
                            </td>
                            <td className="px-3 py-3">
                              <div className="flex items-center gap-1.5">
                                <span className="text-base">{CHARGER_ICONS[c.charger_type] || "⚡"}</span>
                                {c.charger_type && <span className="text-xs font-medium text-slate-600 whitespace-nowrap">{c.charger_type}</span>}
                              </div>
                            </td>
                            <td className="px-3 py-3">
                              <span className="font-mono font-bold text-green-700 text-sm whitespace-nowrap">{c.charger_spec || "—"}</span>
                            </td>
                            <td className="px-3 py-3">
                              <span className="text-sm font-mono text-slate-700">{c.serial_number || "—"}</span>
                            </td>
                            <td className="px-3 py-3">
                              {c.body_type ? (
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${c.body_type === "Metal Body" ? "bg-slate-100 text-slate-700 border-slate-300" : "bg-sky-50 text-sky-700 border-sky-200"}`}>
                                  {c.body_type}
                                </span>
                              ) : <span className="text-slate-400 text-xs">—</span>}
                            </td>
                            <td className="px-3 py-3">
                              {showWarranty ? (
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium border whitespace-nowrap ${WARRANTY_COLORS[c.warranty_status] || "bg-slate-100 text-slate-500 border-slate-200"}`}>
                                  {c.warranty_status}
                                </span>
                              ) : <span className="text-slate-400 text-xs">—</span>}
                            </td>
                            <td className="px-3 py-3">
                              <span className="text-xs text-slate-600 whitespace-nowrap">{purchaseStr || "—"}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Courier Details */}
            {job.courier_details?.awb_number && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center">
                    <Truck size={15} className="text-blue-600" />
                  </div>
                  <h2 className="text-sm font-bold text-slate-900" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
                    Courier / Dispatch
                  </h2>
                </div>
                <div className="space-y-2">
                  {job.courier_details.courier_name && (
                    <div className="flex justify-between items-center py-2 border-b border-slate-50">
                      <span className="text-xs text-slate-500">Courier Service</span>
                      <span className="text-sm font-semibold text-slate-800">{job.courier_details.courier_name}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-2 border-b border-slate-50">
                    <span className="text-xs text-slate-500">AWB / Tracking No.</span>
                    <span className="text-sm font-mono font-bold text-blue-700">{job.courier_details.awb_number}</span>
                  </div>
                  {job.courier_details.tracking_url && (
                    <a href={job.courier_details.tracking_url} target="_blank" rel="noreferrer"
                      className="flex items-center justify-center gap-2 w-full mt-2 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold transition-colors">
                      <ExternalLink size={14} /> Track Shipment Online
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* 9-step Timeline */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <h2 className="text-sm font-bold text-slate-900 mb-4" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
                Service Journey — Step {currentIdx + 1} of {LEGACY_PIPELINE.length}
              </h2>
              <div className="relative">
                <div className="absolute left-4 top-3 bottom-3 w-0.5 bg-slate-100" />
                <div className="space-y-0">
                  {LEGACY_PIPELINE.map((step, i) => {
                    const done    = i < currentIdx;
                    const current = i === currentIdx;
                    const future  = i > currentIdx;
                    const hist    = historyMap[step.status];
                    const Icon    = step.icon;
                    return (
                      <div key={step.status} className={`relative flex gap-3.5 pb-4 last:pb-0 ${future ? "opacity-35" : ""}`}>
                        <div className={`relative z-10 flex-shrink-0 w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                          current ? "border-green-500 bg-green-50 shadow-md" : done ? "border-green-300 bg-green-50" : "border-slate-200 bg-white"
                        }`}>
                          {current
                            ? <div className="relative">
                                <div className="absolute inset-0 rounded-full animate-ping opacity-25" style={{ background: step.dot }} />
                                <Icon size={13} style={{ color: step.dot }} className="relative z-10" />
                              </div>
                            : <Icon size={13} className={done ? "text-green-600" : "text-slate-300"} />}
                        </div>
                        <div className="flex-1 min-w-0 pt-1">
                          <div className="flex items-center justify-between flex-wrap gap-1">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm">{step.emoji}</span>
                              <span className={`text-sm font-semibold ${current ? "text-green-700" : done ? "text-slate-800" : "text-slate-400"}`}>
                                {step.status}
                              </span>
                              {current && (
                                <span className="text-[10px] bg-green-100 text-green-700 font-bold px-2 py-0.5 rounded-full">Current</span>
                              )}
                            </div>
                            {hist && <span className="text-xs text-slate-400">{fmtDT(hist.timestamp)}</span>}
                          </div>
                          <p className={`text-xs mt-0.5 ${done || current ? "text-slate-500" : "text-slate-300"}`}>{step.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Service Center Footer */}
            {sc && (
              <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4">
                <p className="text-xs font-bold text-slate-700 mb-2">{sc.name || "XSTRONG POWER EV CHARGER"}</p>
                <div className="flex flex-wrap gap-3">
                  {sc.phone && (
                    <a href={`tel:${sc.phone}`} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-green-700">
                      <Phone size={10} /> {sc.phone}
                    </a>
                  )}
                  {sc.address && (
                    <span className="flex items-center gap-1.5 text-xs text-slate-500">
                      <MapPin size={10} /> {sc.address}{sc.city ? `, ${sc.city}` : ""}
                    </span>
                  )}
                  {sc.website && (
                    <a href={sc.website.startsWith("http") ? sc.website : `https://${sc.website}`}
                      target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-green-600 hover:text-green-700">
                      <Globe size={10} /> {sc.website}
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
